import { useEffect, useState } from "react";
import { adminApi, getApiErrorMessage } from "@/lib/api";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatCurrency } from "@/lib/format";
import { toast } from "sonner";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { IndianRupee, Upload, Calculator, Trash2, ReceiptText, Pencil, FileDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const COLORS = ["#0b5c46", "#0a9970", "#63b3a0", "#e8a33d", "#7a5195", "#374c80"];

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function DashboardTab() {
  const [d, setD] = useState(null);
  useEffect(() => { adminApi.get("/admin/revenue/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="py-10 text-center text-sm text-[var(--ink-muted)]">Loading revenue analytics...</div>;
  const t = d.totals;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
        {[["Gross Trail" + (d.latest_month ? ` (${d.latest_month})` : ""), formatCurrency(t.gross), "rev-gross"],
          ["GST @18%", formatCurrency(t.gst), "rev-gst"],
          ["TDS @5%", formatCurrency(t.tds), "rev-tds"],
          ["Net Receivable", formatCurrency(t.net), "rev-net"],
          ["Withheld (KYC)", formatCurrency(t.withheld), "rev-withheld"],
          ["Yield (annualized)", `${d.annualized_yield_bps} bps`, "rev-yield"]].map(([l, v, tid]) => (
          <Card key={l} className="glass-card border-0"><CardContent className="p-4">
            <div className="text-xs text-[var(--ink-muted)]">{l}</div>
            <div className="mt-1 font-heading text-lg text-[var(--brand)]" data-testid={tid}>{v}</div>
          </CardContent></Card>
        ))}
      </div>
      {d.product_lines && (
        <Card className="border-[var(--glass-border)]"><CardContent className="flex flex-wrap items-center gap-8 p-4" data-testid="rev-product-lines">
          <div className="text-xs font-medium text-[var(--brand)]">Revenue run-rate<br /><span className="font-heading text-lg">{formatCurrency(d.total_monthly_run_rate)}/mo</span></div>
          {d.product_lines.map((p) => (
            <div key={p.name}>
              <div className="text-xs text-[var(--ink-muted)]">{p.name}</div>
              <div className="font-heading text-base text-[var(--brand)]">{formatCurrency(p.monthly)}{p.one_time ? "" : "/mo"}</div>
            </div>
          ))}
        </CardContent></Card>
      )}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="border-[var(--glass-border)]">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-[var(--brand)]">Expected vs Received (6M)</CardTitle></CardHeader>
          <CardContent className="h-60">
            <ResponsiveContainer width="100%" height={230} minWidth={0}>
              <BarChart data={d.trend}>
                <XAxis dataKey="month" tick={{ fontSize: 9 }} />
                <YAxis tick={{ fontSize: 9 }} width={50} />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="expected" name="Expected" fill="#0b5c46" radius={[3, 3, 0, 0]} />
                <Bar dataKey="received" name="Received" fill="#0a9970" radius={[3, 3, 0, 0]} />
                <Bar dataKey="withheld" name="Withheld" fill="#e8a33d" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card className="border-[var(--glass-border)]">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-[var(--brand)]">Trail by AMC</CardTitle></CardHeader>
          <CardContent className="h-60">
            <ResponsiveContainer width="100%" height={230} minWidth={0}>
              <PieChart>
                <Pie data={d.by_amc} dataKey="value" nameKey="name" innerRadius={45} outerRadius={72} paddingAngle={2}>
                  {d.by_amc.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="border-[var(--glass-border)]">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-[var(--brand)]">Top Revenue Clients</CardTitle></CardHeader>
          <CardContent className="space-y-2 pt-1" data-testid="rev-top-clients">
            {d.top_clients.map((c, i) => {
              const max = d.top_clients[0]?.value || 1;
              return (
                <div key={c.name}>
                  <div className="flex justify-between text-xs"><span>{c.name}</span><span className="text-[var(--ink-muted)]">{formatCurrency(c.value)}/mo</span></div>
                  <div className="mt-0.5 h-1.5 rounded-full bg-[var(--surface-2)]"><div className="h-1.5 rounded-full" style={{ width: `${(c.value / max) * 100}%`, background: COLORS[i % COLORS.length] }} /></div>
                </div>
              );
            })}
            {d.top_clients.length === 0 && <div className="text-sm text-[var(--ink-muted)]">Run a monthly accrual to see client-wise revenue.</div>}
          </CardContent>
        </Card>
        <Card className="border-[var(--glass-border)]">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-[var(--brand)]">Trail by Asset Class</CardTitle></CardHeader>
          <CardContent className="space-y-2 pt-1">
            {d.by_asset_class.map((c, i) => {
              const max = d.by_asset_class[0]?.value || 1;
              return (
                <div key={c.name}>
                  <div className="flex justify-between text-xs"><span>{c.name}</span><span className="text-[var(--ink-muted)]">{formatCurrency(c.value)}/mo</span></div>
                  <div className="mt-0.5 h-1.5 rounded-full bg-[var(--surface-2)]"><div className="h-1.5 rounded-full" style={{ width: `${(c.value / max) * 100}%`, background: COLORS[i % COLORS.length] }} /></div>
                </div>
              );
            })}
            <div className="pt-2 text-xs text-[var(--ink-muted)]">Withheld commission book (WBR95): <span className="font-medium text-amber-700">{formatCurrency(d.withheld_book)}</span></div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function RateCardsTab() {
  const [rates, setRates] = useState([]);
  const [form, setForm] = useState({ amc_name: "", asset_class: "EQUITY", trail_bps: "" });
  const load = () => adminApi.get("/admin/revenue/rate-cards").then((r) => setRates(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await adminApi.post("/admin/revenue/rate-cards", {
        amc_name: form.amc_name.trim() || null, asset_class: form.asset_class, trail_bps: parseFloat(form.trail_bps),
      });
      toast.success("Rate card saved");
      setForm({ amc_name: "", asset_class: "EQUITY", trail_bps: "" });
      load();
    } catch (err) { toast.error(getApiErrorMessage(err)); }
  };
  const remove = async (id) => {
    try { await adminApi.delete(`/admin/revenue/rate-cards/${id}`); toast.success("Override removed"); load(); }
    catch (err) { toast.error(getApiErrorMessage(err)); }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[340px_1fr]">
      <Card className="h-fit border-[var(--glass-border)]">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-[var(--brand)]">Add / Edit Rate</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <label className="text-xs text-[var(--ink-muted)]">AMC (blank = default for asset class)</label>
            <Input value={form.amc_name} onChange={(e) => setForm({ ...form, amc_name: e.target.value })} placeholder="e.g. HDFC Mutual Fund" data-testid="rate-amc-input" />
          </div>
          <div>
            <label className="text-xs text-[var(--ink-muted)]">Asset class</label>
            <select className="w-full rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={form.asset_class} onChange={(e) => setForm({ ...form, asset_class: e.target.value })} data-testid="rate-class-select">
              {["EQUITY", "HYBRID", "DEBT", "SOLUTION", "OTHER"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-[var(--ink-muted)]">Trail rate (bps p.a., e.g. 90 = 0.90%)</label>
            <Input type="number" value={form.trail_bps} onChange={(e) => setForm({ ...form, trail_bps: e.target.value })} data-testid="rate-bps-input" />
          </div>
          <Button className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56]" onClick={save} disabled={!form.trail_bps} data-testid="rate-save-button">Save rate</Button>
        </CardContent>
      </Card>
      <Card className="border-[var(--glass-border)]"><CardContent className="p-0">
        {rates.length === 0 && <div className="border-b border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">No commission rate cards are configured. Accruals will be marked RATE_MISSING; the system will not invent a trail rate.</div>}
        <table className="w-full text-sm" data-testid="rate-cards-table">
          <thead><tr className="border-b border-[var(--glass-border)] text-left text-xs text-[var(--ink-muted)]">
            <th className="p-3 font-medium">AMC</th><th className="p-3 font-medium">Asset Class</th><th className="p-3 text-right font-medium">Trail (bps p.a.)</th><th className="p-3" />
          </tr></thead>
          <tbody className="divide-y divide-[var(--glass-border)]">
            {rates.map((r) => (
              <tr key={r.id}>
                <td className="p-3">{r.amc_name || <span className="text-[var(--ink-muted)]">All AMCs (default)</span>}</td>
                <td className="p-3">{r.asset_class}</td>
                <td className="p-3 text-right font-medium">{r.trail_bps} <span className="text-xs text-[var(--ink-muted)]">({(r.trail_bps / 100).toFixed(2)}%)</span></td>
                <td className="p-3 text-right">
                  <button className="text-red-500 hover:text-red-700" onClick={() => remove(r.id)} data-testid={`rate-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}

function FeedsTab() {
  const [month, setMonth] = useState(currentMonth());
  const [rta, setRta] = useState("CAMS");
  const [busy, setBusy] = useState(false);
  const [recons, setRecons] = useState([]);
  const [detail, setDetail] = useState(null);

  const load = () => adminApi.get("/admin/revenue/reconciliations").then((r) => setRecons(r.data));
  useEffect(() => { load(); }, []);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); load(); }
    catch (err) { toast.error(getApiErrorMessage(err)); }
    finally { setBusy(false); }
  };

  const accrue = () => run(async () => {
    const { data } = await adminApi.post("/admin/revenue/accrue", { month });
    toast.success(`Accrued ${data.rows} rows — gross ${formatCurrency(data.gross)}, withheld ${formatCurrency(data.withheld)}, ${data.rate_missing || 0} missing rate(s), ${data.nav_missing || 0} missing NAV(s)`);
  });
  const upload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    run(async () => {
      const text = await file.text();
      const { data } = await adminApi.post("/admin/revenue/feeds/upload", { file_name: file.name, file_content: text });
      if (data.reconciliations?.length) {
        toast.success(`${data.feed_type}: ${data.rows} rows imported — reconciliation ${data.reconciliations[0].status}`);
        setDetail(data.reconciliations[0]);
      } else {
        toast.success(`${data.feed_type}: ${data.rows} rows imported`);
      }
    });
    e.target.value = "";
  };
  const openDetail = async (id) => {
    const { data } = await adminApi.get(`/admin/revenue/reconciliations/${id}`);
    setDetail(data);
  };

  const sections = detail ? [
    ["Short paid", detail.short_paid, "amber"], ["Over paid", detail.over_paid, "amber"],
    ["Missing at RTA", detail.missing_at_rta, "red"], ["Unexpected receipts", detail.unexpected, "red"],
  ] : [];

  return (
    <div className="space-y-6">
      <Card className="glass-card border-0"><CardContent className="flex flex-wrap items-center gap-3 p-5">
        <input type="month" className="rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="rev-month-input" />
        <select className="rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={rta} onChange={(e) => setRta(e.target.value)} data-testid="rev-rta-select">
          <option value="CAMS">CAMS — WBR77 Brokerage</option>
          <option value="KFINTECH">KFintech — MFSD205 Brokerage</option>
          <option value="WBR95">CAMS — WBR95 Withheld</option>
        </select>
        <Button variant="outline" className="rounded-full" disabled={busy} onClick={accrue} data-testid="rev-accrue-button">
          <Calculator className="mr-1 h-4 w-4" /> Run trail accrual
        </Button>
        <label className={`flex cursor-pointer items-center gap-1.5 rounded-full bg-[var(--cta)] px-4 py-2 text-sm font-medium text-white ${busy ? "opacity-60" : ""}`}>
          <Upload className="h-4 w-4" /> {busy ? "Processing..." : "Upload brokerage file"}
          <input type="file" accept=".csv,.txt" className="hidden" onChange={upload} disabled={busy} data-testid="rev-upload-input" />
        </label>
      </CardContent></Card>

      <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
        <Card className="h-fit border-[var(--glass-border)]"><CardContent className="p-0">
          {recons.length === 0 ? <div className="p-5 text-sm text-[var(--ink-muted)]" data-testid="rev-no-recons">No revenue reconciliations yet — upload a WBR77/MFSD205 file.</div> : (
            <div className="divide-y divide-[var(--glass-border)]">
              {recons.map((r) => (
                <button key={r.id} className={`block w-full p-3 text-left hover:bg-[var(--surface-2)] ${detail?.id === r.id ? "bg-[var(--surface-2)]" : ""}`} onClick={() => openDetail(r.id)} data-testid={`rev-recon-row-${r.id}`}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold">{r.id} · {r.month}</span>
                    <span className={`rounded-full px-2 py-0.5 font-medium ${r.status === "CLEAN" ? "bg-emerald-50 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>{r.status}</span>
                  </div>
                  <div className="mt-1 text-[11px] text-[var(--ink-muted)]">
                    Expected {formatCurrency(r.expected_total)} · Received {formatCurrency(r.received_total)} · Δ {formatCurrency(r.variance_total)}
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent></Card>
        <Card className="border-[var(--glass-border)]">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-[var(--brand)]">{detail ? `${detail.id} — ${detail.month}` : "Select a reconciliation"}</CardTitle></CardHeader>
          <CardContent>
            {detail ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-2 text-center min-[420px]:grid-cols-3">
                  {[["Expected", formatCurrency(detail.expected_total)], ["Received", formatCurrency(detail.received_total)], ["Variance", formatCurrency(detail.variance_total)]].map(([l, v]) => (
                    <div key={l} className="rounded-xl bg-[var(--surface-2)] p-2">
                      <div className="text-[10px] uppercase text-[var(--ink-muted)]">{l}</div>
                      <div className="font-heading text-base text-[var(--brand)]" data-testid={`rev-recon-${l.toLowerCase()}`}>{v}</div>
                    </div>
                  ))}
                </div>
                {sections.map(([label, rows, tone]) => rows?.length > 0 && (
                  <div key={label}>
                    <div className={`mb-1 text-xs font-semibold ${tone === "red" ? "text-red-600" : "text-amber-600"}`}>{label} ({rows.length})</div>
                    <div className="divide-y divide-[var(--glass-border)] rounded-xl border border-[var(--glass-border)]">
                      {rows.map((r, i) => (
                        <div key={i} className="p-2.5 text-xs">
                          <div className="font-medium">{r.scheme_name?.slice(0, 60)}</div>
                          <div className="text-[var(--ink-muted)]">Expected {formatCurrency(r.expected_gross)} · Received {formatCurrency(r.received_gross)} · <span className={r.variance < 0 ? "text-red-600" : "text-amber-700"}>Δ {formatCurrency(r.variance)}</span></div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {detail.status === "CLEAN" && <div className="rounded-xl bg-emerald-50 p-4 text-sm text-emerald-700">All {detail.matched_count} schemes reconciled to the rupee. Payout is clean.</div>}
              </div>
            ) : <div className="py-10 text-center text-sm text-[var(--ink-muted)]">Upload a brokerage file or pick a run to inspect payout variances.</div>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function WithheldTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { adminApi.get("/admin/revenue/withheld").then((r) => setRows(r.data)); }, []);
  return (
    <Card className="border-[var(--glass-border)]"><CardContent className="overflow-x-auto p-0">
      {rows.length === 0 ? <div className="p-5 text-sm text-[var(--ink-muted)]" data-testid="rev-no-withheld">No withheld commissions on record — upload a WBR95 file.</div> : (
        <table className="w-full text-xs" data-testid="rev-withheld-table">
          <thead><tr className="border-b border-[var(--glass-border)] text-left text-[var(--ink-muted)]">
            <th className="p-3 font-medium">Folio</th><th className="p-3 font-medium">Investor</th><th className="p-3 font-medium">Scheme</th><th className="p-3 font-medium">Period</th><th className="p-3 text-right font-medium">Amount</th><th className="p-3 font-medium">Reason</th><th className="p-3 font-medium">Status</th>
          </tr></thead>
          <tbody className="divide-y divide-[var(--glass-border)]">
            {rows.map((w, i) => (
              <tr key={i}>
                <td className="p-3 font-medium">{w.folio}</td>
                <td className="p-3">{w.inv_name}</td>
                <td className="max-w-[240px] truncate p-3">{w.scheme_name}</td>
                <td className="p-3">{w.period}</td>
                <td className="p-3 text-right font-medium text-amber-700">{formatCurrency(w.amount)}</td>
                <td className="p-3">{w.reason}</td>
                <td className="p-3"><span className="rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-800">{w.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </CardContent></Card>
  );
}

function GstTab() {
  const [mfrs, setMfrs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [company, setCompany] = useState(null);
  const [month, setMonth] = useState(currentMonth());
  const [editMfr, setEditMfr] = useState(null);
  const [showCompany, setShowCompany] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => Promise.all([
    adminApi.get("/admin/revenue/manufacturers").then((r) => setMfrs(r.data)),
    adminApi.get("/admin/revenue/gst/invoices").then((r) => setInvoices(r.data)),
    adminApi.get("/admin/revenue/company-profile").then((r) => setCompany(r.data)),
  ]);
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/revenue/gst/generate", { month });
      toast.success(`${data.created} invoice(s) generated for ${month}${data.skipped_existing ? ` (${data.skipped_existing} already existed)` : ""} — taxable ${formatCurrency(data.total_taxable)}`);
      load();
    } catch (err) { toast.error(getApiErrorMessage(err)); }
    finally { setBusy(false); }
  };
  const saveMfr = async () => {
    try {
      await adminApi.put(`/admin/revenue/manufacturers/${editMfr.id}`, {
        gstin: editMfr.gstin, pan: editMfr.pan, address: editMfr.address,
        state: editMfr.state, state_code: editMfr.state_code, email: editMfr.email,
      });
      toast.success("Manufacturer updated"); setEditMfr(null); load();
    } catch (err) { toast.error(getApiErrorMessage(err)); }
  };
  const saveCompany = async () => {
    try {
      const { data } = await adminApi.put("/admin/revenue/company-profile", company);
      setCompany(data); setShowCompany(false); toast.success("Company GST profile saved");
    } catch (err) { toast.error(getApiErrorMessage(err)); }
  };
  const setStatus = async (id, status) => {
    try { await adminApi.post(`/admin/revenue/gst/invoices/${id}/status`, { status }); load(); }
    catch (err) { toast.error(getApiErrorMessage(err)); }
  };
  const downloadPdf = async (inv) => {
    try {
      const resp = await adminApi.get(`/admin/revenue/gst/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(resp.data);
      a.download = `${inv.invoice_no.replaceAll("/", "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Invoice PDF downloaded");
    } catch (err) { toast.error(getApiErrorMessage(err)); }
  };

  const TYPE_LABEL = { AMC: "AMC (MF)", AIF_MANAGER: "AIF Manager", PMS_MANAGER: "PMS Manager" };
  const STATUS_TONE = { DRAFT: "bg-slate-100 text-slate-700", ISSUED: "bg-blue-50 text-blue-700", PAID: "bg-emerald-50 text-emerald-700", CANCELLED: "bg-red-50 text-red-600" };

  return (
    <div className="space-y-6">
      <Card className="glass-card border-0"><CardContent className="flex flex-wrap items-center gap-3 p-5">
        <input type="month" className="rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="gst-month-input" />
        <Button className="rounded-full bg-[var(--cta)] hover:bg-[#087a56]" disabled={busy} onClick={generate} data-testid="gst-generate-button">
          <ReceiptText className="mr-1 h-4 w-4" /> {busy ? "Generating..." : "Generate GST invoices"}
        </Button>
        <Button variant="outline" className="rounded-full" onClick={() => setShowCompany(true)} data-testid="gst-company-button">
          <Pencil className="mr-1 h-4 w-4" /> SMIFS GST profile {company ? `(${company.gstin})` : ""}
        </Button>
        <span className="text-xs text-[var(--ink-muted)]">One consolidated tax invoice per manufacturer per month · SAC 997152 · CGST+SGST (same state) / IGST</span>
      </CardContent></Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="border-[var(--glass-border)]">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-[var(--brand)]">Manufacturer Master ({mfrs.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-xs" data-testid="gst-manufacturers-table">
              <thead><tr className="border-b border-[var(--glass-border)] text-left text-[var(--ink-muted)]">
                <th className="p-3 font-medium">Manufacturer</th><th className="p-3 font-medium">Type</th><th className="p-3 font-medium">GSTIN</th><th className="p-3 font-medium">State</th><th className="p-3" />
              </tr></thead>
              <tbody className="divide-y divide-[var(--glass-border)]">
                {mfrs.map((m) => (
                  <tr key={m.id} data-testid={`gst-mfr-row-${m.id}`}>
                    <td className="p-3 font-medium text-[var(--brand)]">{m.name}</td>
                    <td className="p-3">{TYPE_LABEL[m.type]}</td>
                    <td className="p-3">{m.gstin || <span className="text-amber-700">Pending</span>}</td>
                    <td className="p-3">{m.state} ({m.state_code})</td>
                    <td className="p-3 text-right"><button className="text-[var(--cta)] hover:underline" onClick={() => setEditMfr({ ...m })} data-testid={`gst-mfr-edit-${m.id}`}><Pencil className="h-3.5 w-3.5" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card className="border-[var(--glass-border)]">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-[var(--brand)]">Invoice Register ({invoices.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            {invoices.length === 0 ? <div className="p-5 text-sm text-[var(--ink-muted)]" data-testid="gst-no-invoices">No GST invoices yet — pick a month and generate.</div> : (
              <table className="w-full text-xs" data-testid="gst-invoices-table">
                <thead><tr className="border-b border-[var(--glass-border)] text-left text-[var(--ink-muted)]">
                  <th className="p-3 font-medium">Invoice</th><th className="p-3 text-right font-medium">Taxable</th><th className="p-3 text-right font-medium">GST</th><th className="p-3 text-right font-medium">Total</th><th className="p-3 font-medium">Status</th><th className="p-3" />
                </tr></thead>
                <tbody className="divide-y divide-[var(--glass-border)]">
                  {invoices.map((inv) => (
                    <tr key={inv.id} data-testid={`gst-invoice-row-${inv.id}`}>
                      <td className="p-3"><div className="font-medium text-[var(--brand)]">{inv.invoice_no}</div><div className="text-[var(--ink-muted)]">{inv.manufacturer_name} · {inv.month}</div></td>
                      <td className="p-3 text-right">{formatCurrency(inv.taxable_value)}</td>
                      <td className="p-3 text-right">{formatCurrency(inv.cgst + inv.sgst + inv.igst)} <span className="text-[var(--ink-muted)]">{inv.igst ? "IGST" : "C+S"}</span></td>
                      <td className="p-3 text-right font-medium">{formatCurrency(inv.total)}</td>
                      <td className="p-3">
                        <select className={`rounded-full border-0 px-2 py-0.5 text-[10px] font-medium ${STATUS_TONE[inv.status]}`} value={inv.status} onChange={(e) => setStatus(inv.id, e.target.value)} data-testid={`gst-status-${inv.id}`}>
                          {["DRAFT", "ISSUED", "PAID", "CANCELLED"].map((s) => <option key={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="p-3 text-right"><button className="text-[var(--cta)]" onClick={() => downloadPdf(inv)} data-testid={`gst-pdf-${inv.id}`}><FileDown className="h-4 w-4" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!editMfr} onOpenChange={(o) => !o && setEditMfr(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-[var(--brand)]">{editMfr?.name}</DialogTitle><DialogDescription className="text-xs">GST details for tax invoicing · state code decides CGST+SGST vs IGST</DialogDescription></DialogHeader>
          {editMfr && (
            <div className="space-y-3">
              <div><label className="text-xs text-[var(--ink-muted)]">GSTIN (15 chars)</label><Input value={editMfr.gstin} onChange={(e) => setEditMfr({ ...editMfr, gstin: e.target.value })} data-testid="gst-mfr-gstin-input" /></div>
              <div><label className="text-xs text-[var(--ink-muted)]">PAN</label><Input value={editMfr.pan} onChange={(e) => setEditMfr({ ...editMfr, pan: e.target.value })} /></div>
              <div><label className="text-xs text-[var(--ink-muted)]">Registered address</label><Input value={editMfr.address} onChange={(e) => setEditMfr({ ...editMfr, address: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-[var(--ink-muted)]">State</label><Input value={editMfr.state} onChange={(e) => setEditMfr({ ...editMfr, state: e.target.value })} /></div>
                <div><label className="text-xs text-[var(--ink-muted)]">State code</label><Input value={editMfr.state_code} onChange={(e) => setEditMfr({ ...editMfr, state_code: e.target.value })} data-testid="gst-mfr-state-code-input" /></div>
              </div>
              <div><label className="text-xs text-[var(--ink-muted)]">Accounts email</label><Input value={editMfr.email} onChange={(e) => setEditMfr({ ...editMfr, email: e.target.value })} /></div>
              <Button className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56]" onClick={saveMfr} data-testid="gst-mfr-save-button">Save manufacturer</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showCompany} onOpenChange={setShowCompany}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-[var(--brand)]">SMIFS GST Profile</DialogTitle><DialogDescription className="text-xs">Appears as the supplier on every tax invoice</DialogDescription></DialogHeader>
          {company && (
            <div className="space-y-3">
              {[["Legal name", "name"], ["GSTIN", "gstin"], ["PAN", "pan"], ["ARN", "arn"], ["Registered address", "address"], ["Bank details (remittance)", "bank_details"]].map(([l, k]) => (
                <div key={k}><label className="text-xs text-[var(--ink-muted)]">{l}</label><Input value={company[k] || ""} onChange={(e) => setCompany({ ...company, [k]: e.target.value })} data-testid={`gst-company-${k}-input`} /></div>
              ))}
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-[var(--ink-muted)]">State</label><Input value={company.state || ""} onChange={(e) => setCompany({ ...company, state: e.target.value })} /></div>
                <div><label className="text-xs text-[var(--ink-muted)]">State code</label><Input value={company.state_code || ""} onChange={(e) => setCompany({ ...company, state_code: e.target.value })} /></div>
              </div>
              <Button className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56]" onClick={saveCompany} data-testid="gst-company-save-button">Save profile</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Revenue() {
  return (
    <AdminLayout>
      <div className="mb-6">
        <h1 className="flex items-center gap-2 font-heading text-3xl text-[var(--brand)]"><IndianRupee className="h-7 w-7" /> Revenue</h1>
        <p className="mt-1 text-sm text-[var(--ink-muted)]">Trail commission engine over the canonical ARN book — administrator rate cards, actual CAMS/KFintech payouts and reconciliation without fallback estimates.</p>
      </div>
      <Tabs defaultValue="dashboard">
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="rev-tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="rates" data-testid="rev-tab-rates">Rate Cards</TabsTrigger>
          <TabsTrigger value="feeds" data-testid="rev-tab-feeds">Brokerage Feeds</TabsTrigger>
          <TabsTrigger value="withheld" data-testid="rev-tab-withheld">Withheld / Clawback</TabsTrigger>
          <TabsTrigger value="gst" data-testid="rev-tab-gst">GST Billing</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard"><DashboardTab /></TabsContent>
        <TabsContent value="rates"><RateCardsTab /></TabsContent>
        <TabsContent value="feeds"><FeedsTab /></TabsContent>
        <TabsContent value="withheld"><WithheldTab /></TabsContent>
        <TabsContent value="gst"><GstTab /></TabsContent>
      </Tabs>
    </AdminLayout>
  );
}
